import Button from "rsuite/Button";
import Message from "rsuite/Message";
import { FiCheck, FiPlus, FiSave, FiTrash2, FiX } from "react-icons/fi";
import { OrderListDeliverySetting } from "../../models/orderListModels";
import { getOrderListSettingsErrors } from "../../helpers/orderListSettingsHelper";

interface Props {
  draftSettings: OrderListDeliverySetting[];
  activeCategories: string[];
  open: boolean;
  saving: boolean;
  error: unknown;
  onClose: () => void;
  onSave: (settings: OrderListDeliverySetting[]) => void;
  onChange: (settings: OrderListDeliverySetting[]) => void;
  onDelete: (id: string) => void;
  onAddSetting: () => void;
}

const deliveryDays = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"] as const;

const OrderListSettingsPanel = ({ draftSettings, activeCategories, open, saving, error, onClose, onSave, onChange, onDelete, onAddSetting }: Props): JSX.Element | null => {
  if (!open) {
    return null;
  }

  const updateSetting = (id: string, update: Partial<OrderListDeliverySetting>): void => {
    onChange(draftSettings.map(setting => setting.id === id ? { ...setting, ...update } : setting));
  };

  const confirmDelete = (setting: OrderListDeliverySetting): void => {
    if (window.confirm(`Delete delivery setting "${setting.name}"? This cannot be undone once saved.`)) {
      onDelete(setting.id);
    }
  };

  return <section className="order-list-settings-panel" aria-label="Delivery settings">
    {draftSettings.map(setting => <div className="order-list-setting-editor" key={setting.id}>
      <div className="order-list-setting-editor__topline">
        <input aria-label="Setting name" value={setting.name} onChange={event => updateSetting(setting.id, { name: event.target.value })} />
        <Button
          className="order-list-setting-editor__action-button"
          appearance="ghost"
          color={setting.active ? "blue" : undefined}
          onClick={() => updateSetting(setting.id, { active: !setting.active })}
        >
          {setting.active ? <FiCheck /> : <FiX />}<span>{setting.active ? "Active" : "Inactive"}</span>
        </Button>
        <Button className="order-list-setting-editor__action-button order-list-setting-editor__delete" appearance="ghost" color="red" onClick={() => confirmDelete(setting)}>
          <FiTrash2 /><span>Delete</span>
        </Button>
      </div>
      <div className="order-list-setting-editor__choices">
        <fieldset><legend>Categories</legend>
          {Array.from(new Set([...activeCategories, ...setting.categories]))
            .sort((left, right) => left.localeCompare(right, undefined, { sensitivity: "base" }))
            .map(value => {
              const isStale = !activeCategories.includes(value);
              return <label key={value} className={isStale ? "order-list-setting-editor__stale-category" : undefined}>
                <input type="checkbox" checked={setting.categories.includes(value)} onChange={event => updateSetting(setting.id, { categories: event.target.checked ? [...setting.categories, value] : setting.categories.filter(category => category !== value) })} />
                {value}{isStale && " (renamed or inactive - uncheck to remove)"}
              </label>;
            })}
        </fieldset>
        <fieldset><legend>Delivery days</legend>
          {deliveryDays.map(value => <label key={value}><input type="checkbox" checked={setting.deliveryDays.includes(value)} onChange={event => updateSetting(setting.id, { deliveryDays: event.target.checked ? [...setting.deliveryDays, value] : setting.deliveryDays.filter(day => day !== value) })} /> {value}</label>)}
        </fieldset>
      </div>
    </div>)}
    {error && <Message type="error" showIcon>
      <div>Settings could not be saved.</div>
      <ul>{getOrderListSettingsErrors(error).map(message => <li key={message}>{message}</li>)}</ul>
    </Message>}
    <div className="order-list-settings-panel__actions">
      <Button className="order-list-settings-panel__save" appearance="primary" loading={saving} onClick={() => onSave(draftSettings)}>
        <FiSave /><span>Save delivery settings</span>
      </Button>
      <Button className="order-list-settings-panel__add" appearance="ghost" onClick={onAddSetting}>
        <FiPlus /><span>Add delivery settings</span>
      </Button>
    </div>
    <Button className="order-list-settings-panel__close" appearance="subtle" onClick={onClose}>Close settings</Button>
  </section>;
};

export default OrderListSettingsPanel;